// src/components/database/productbin.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DeleteOutlined } from '@ant-design/icons';
// Import Ant Design components
import { Steps, Button, message, Spin, Form, Input, Select, Table, Checkbox, ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import { NODE_BACKEND_API_URL } from '../../config';
import '../../pages/binpage.css';

const { Step } = Steps;
const { Option } = Select;

// Base URL for your backend API
const API_BASE_URL = NODE_BACKEND_API_URL;

function ProductBin() {
    // --- State Variables ---
    const [currentStep, setCurrentStep] = useState(0); // Start at Step 0: Action
    const [actionType, setActionType] = useState(null); // 'erase' or 'restore'

    const [brands, setBrands] = useState([]); // To display BrandName in results
    const [searchProductCode, setSearchProductCode] = useState('');
    const [foundProduct, setFoundProduct] = useState(null); // The product found by search
    const [confirmationChecked, setConfirmationChecked] = useState(false); // For Step 3 checkbox

    const [loadingBrands, setLoadingBrands] = useState(true);
    const [isSearching, setIsSearching] = useState(false); // Search specific loading
    const [isSubmittingAction, setIsSubmittingAction] = useState(false); // Delete/Restore action loading

    const [actionResult, setActionResult] = useState(null); // { success: true/false, message: string, product: object }

    // Ant Design Form instance for validation (used for product code input)
    const [form] = Form.useForm();

    // Define the steps configuration
    const steps = [
        { title: 'Acción' }, // Erase or Restore
        { title: 'Producto' }, // Search Product Code
        { title: 'Validar Acción' }, // Confirm & Perform Action
        { title: 'Resultado' }, // Success/Failure Message
    ];

    // --- Effects ---

    // Effect to fetch the list of brands on component mount (needed for displaying BrandName)
    useEffect(() => {
        axios.get(`${API_BASE_URL}/getallbrands`)
            .then(res => {
                const brandOptions = res.data.map(brand => ({
                    value: String(brand.BrandId),
                    label: brand.BrandName
                }));
                setBrands(brandOptions);
                setLoadingBrands(false);
            })
            .catch(er => {
                console.error("Error fetching brands:", er);
                message.error("Error al cargar la lista de marcas.");
                setLoadingBrands(false);
            });
    }, []);

    // --- Handlers ---

    // Resets all state to initial for a new action
    const resetAllState = () => {
        setCurrentStep(0);
        setActionType(null);
        setSearchProductCode('');
        setFoundProduct(null);
        setConfirmationChecked(false);
        setIsSearching(false);
        setIsSubmittingAction(false);
        setActionResult(null);
        form.resetFields(); // Clear AntD form fields
    };

    // Handles "Eliminar Producto" button click (Step 1)
    const handleChooseErase = () => {
        setActionType('erase');
        setCurrentStep(1); // Move to Step 2 (Product Search)
        setSearchProductCode(''); // Clear previous search
        setFoundProduct(null); // Clear any previously found product
        form.resetFields(); // Clear form fields
    };

    // Handles "Restaurar Producto Eliminado" button click (Step 1)
    const handleChooseRestore = () => {
        setActionType('restore');
        setCurrentStep(1); // Move to Step 2 (Product Search)
        setSearchProductCode(''); // Clear previous search
        setFoundProduct(null); // Clear any previously found product
        form.resetFields(); // Clear form fields
    };

    // Handles product code input change (Step 2)
    const handleProductCodeInputChange = (e) => {
        setSearchProductCode(e.target.value);
        setFoundProduct(null); // Clear found product when input changes
    };

    // Handles search button click (Step 2)
    const handleSearchProduct = async () => {
        if (!searchProductCode.trim()) {
            message.warning("Por favor, ingrese un código de producto para buscar.");
            return;
        }
        if (!actionType) {
            message.error("Error interno: Tipo de acción no definido."); // Should not happen
            return;
        }

        setIsSearching(true);
        setFoundProduct(null); // Clear previous product
        setConfirmationChecked(false); // Reset confirmation
        setActionResult(null); // Clear previous result

        try {
            let url;
            if (actionType === 'erase') {
                url = `${API_BASE_URL}/managersearch?search=${encodeURIComponent(searchProductCode)}`;
            } else { // actionType === 'restore'
                url = `${API_BASE_URL}/auditsearch?search=${encodeURIComponent(searchProductCode)}`;
            }

            const response = await axios.get(url);
            if (response.data && response.data.length > 0) {
                const product = response.data[0];
                // Check IsDeleted status matches actionType expectations
                if (actionType === 'erase' && product.IsDeleted) {
                    message.warning(`El producto con código "${product.ProductCode}" ya está eliminado.`);
                    setFoundProduct(null); // Don't allow deleting an already deleted product
                } else if (actionType === 'restore' && !product.IsDeleted) {
                    message.warning(`El producto con código "${product.ProductCode}" no está eliminado.`);
                    setFoundProduct(null); // Don't allow restoring an active product
                } else {
                    setFoundProduct(product);
                    setCurrentStep(2); // Move to Step 3: Validate Action
                }
            } else {
                message.info(`No se encontró un producto con el código "${searchProductCode}" ${actionType === 'erase' ? 'activo' : 'eliminado'}.`);
                setFoundProduct(null); // Explicitly clear if not found
            }
        } catch (error) {
            console.error("Error during product search:", error);
            message.error("Error al buscar el producto. Intente de nuevo.");
            setFoundProduct(null);
        } finally {
            setIsSearching(false);
        }
    };

    // Handles the "Delete" or "Restore" action (Step 3)
    const handlePerformAction = async () => {
        if (!foundProduct) {
            message.error("Error interno: No hay producto seleccionado para la acción.");
            return;
        }
        if (!confirmationChecked) {
            message.warning("Debe confirmar la acción marcando la casilla.");
            return;
        }

        setIsSubmittingAction(true);
        setActionResult(null); // Clear previous result

        try {
            const response = await axios.post(`${API_BASE_URL}/setisdeleted`, {
                productId: foundProduct.ProductId
            });

            console.log("SetIsDeleted Response:", response.data);

            setActionResult({
                success: true,
                message: actionType === 'erase'
                    ? `Producto "${foundProduct.ProductCode}" eliminado exitosamente.`
                    : `Producto "${foundProduct.ProductCode}" restaurado exitosamente.`,
                product: response.data.updatedProduct // Assuming backend returns updated product
            });
            message.success(actionType === 'erase' ? "Producto eliminado!" : "Producto restaurado!");
            setCurrentStep(3); // Move to Step 4: Results

        } catch (error) {
            console.error("Error performing action:", error);
            let errorMessage = `Error al ${actionType === 'erase' ? 'eliminar' : 'restaurar'} el producto.`;
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = `Error: ${error.response.data.message}`;
            }
            setActionResult({
                success: false,
                message: errorMessage,
                product: foundProduct // Keep product data for context on error
            });
            message.error(errorMessage);
            setCurrentStep(3); // Stay on results step to show error
        } finally {
            setIsSubmittingAction(false);
        }
    };

    // Helper to render product data in a table for display
    const renderProductDetailsTable = (data) => {
        if (!data) return null;

        // Filter out unnecessary fields and map to desired display names
        const displayFields = [
            { key: 'BrandId', title: 'ID Marca', render: (id) => brands.find(b => String(b.value) === String(id))?.label || id },
            { key: 'ProductCode', title: 'Código de Producto' },
            { key: 'ProductDescription', title: 'Descripción' },
            { key: 'EAN', title: 'EAN' },
            { key: 'ProductCost', title: 'Costo' },
            { key: 'IsFavorite', title: 'Favorito', render: (val) => val ? 'Sí' : 'No' },
            { key: 'IsDeleted', title: 'Eliminado', render: (val) => val ? 'Sí' : 'No' },
            { key: 'DeletedDate', title: 'Fecha Eliminación', render: (date) => date ? new Date(date).toLocaleString() : 'N/A' },
            { key: 'DeletedBy', title: 'Eliminado Por' },
            { key: 'ReinstatedDate', title: 'Fecha Restauración', render: (date) => date ? new Date(date).toLocaleString() : 'N/A' },
            { key: 'ReinstatedBy', title: 'Restaurado Por' },
        ];

        const columns = displayFields
            .filter(field => data[field.key] !== undefined && data[field.key] !== null) // Only show fields that exist in the data
            .map(field => ({
                title: field.title,
                dataIndex: field.key,
                key: field.key,
                render: field.render || ((text) => text), // Use custom render if provided
            }));

        const dataSource = [{
            key: data.ProductId || 'product-details',
            ...data
        }];

        return (
            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                size="small"
                style={{ margin: '20px 0', width: '100%' }}
                bordered
            />
        );
    };


    // --- Render Logic ---

    // Initial loading state for brands
    if (loadingBrands) {
        return (
            <div className="bin-page-container">
                <div className="bin-content-container" style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin tip="Cargando lista de marcas..." size="large" />
                </div>
            </div>
        );
    }

    return (
        <ConfigProvider locale={esES}>
            <div className="bin-page-container">
                {/* Header */}
                <div className="bin-header">
                    <h1>
                        <DeleteOutlined />
                        Papelera de Productos
                    </h1>
                </div>

                {/* Steps */}
                <div className="bin-steps-container">
                    <Steps current={currentStep}>
                        {steps.map((item, index) => (
                            <Step key={index} title={item.title} />
                        ))}
                    </Steps>
                </div>

            {/* Content */}
            <div className="bin-content-container">

                {/* --- Step Content Area --- */}
                <div className="steps-content">
                    {/* Step 1: Action (AntD Index 0) */}
                    {currentStep === 0 && (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                                ¿Qué desea hacer?
                            </h3>
                            <Button
                                type="primary"
                                danger
                                size="large"
                                style={{ margin: '0 10px' }}
                                onClick={handleChooseErase}
                            >
                                Eliminar Producto
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                style={{ margin: '0 10px' }}
                                onClick={handleChooseRestore}
                            >
                                Restaurar Producto Eliminado
                            </Button>
                        </div>
                    )}

                    {/* Step 2: Product Search (AntD Index 1) */}
                    {currentStep === 1 && (
                        <div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem' }}>
                                {actionType === 'erase' ? 'Buscar Producto a Eliminar' : 'Buscar Producto Eliminado a Restaurar'}
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                                <label htmlFor="productCodeSearch" className="editor-label" style={{ marginRight: '0.5rem', minWidth: '100px' }}>Código:</label>
                                <Input
                                    id="productCodeSearch"
                                    placeholder="Ingrese el código de producto"
                                    value={searchProductCode}
                                    onChange={handleProductCodeInputChange}
                                    disabled={isSearching}
                                    style={{ flexGrow: 1, marginRight: '0.5rem' }}
                                />
                                <Button
                                    type="primary"
                                    onClick={handleSearchProduct}
                                    loading={isSearching}
                                    disabled={!searchProductCode.trim() || isSearching}
                                >
                                    Buscar
                                </Button>
                            </div>
                            {isSearching && <Spin tip="Buscando producto..." style={{ marginTop: '0.5rem' }} />}
                        </div>
                    )}

                    {/* Step 3: Validate Action (AntD Index 2) */}
                    {currentStep === 2 && (
                        <div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem' }}>
                                {actionType === 'erase' ? 'Confirmar Eliminación' : 'Confirmar Restauración'}
                            </h3>
                            {foundProduct ? (
                                <>
                                    {renderProductDetailsTable(foundProduct)}
                                    <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                        <Checkbox
                                            checked={confirmationChecked}
                                            onChange={(e) => setConfirmationChecked(e.target.checked)}
                                            disabled={isSubmittingAction}
                                        >
                                            Confirmo la acción de {actionType === 'erase' ? 'eliminar' : 'restaurar'} este producto.
                                        </Checkbox>
                                    </div>
                                    <Button
                                        type="primary"
                                        size="large"
                                        danger
                                        onClick={handlePerformAction}
                                        loading={isSubmittingAction}
                                        disabled={!confirmationChecked || isSubmittingAction}
                                    >
                                        {actionType === 'erase' ? 'Eliminar Producto' : 'Restaurar Producto'}
                                    </Button>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>
                                    <p>No se encontró un producto con el código: {actionType === 'erase' ? 'eliminar' : 'restaurar'}.</p>
                                    <Button type="default" onClick={() => setCurrentStep(1)} style={{ marginTop: '1rem' }}>
                                        Volver a Buscar
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 4: Results (AntD Index 3) */}
                    {currentStep === 3 && actionResult && (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: actionResult.success ? '#0000FF' : 'red' }}>
                                {actionResult.success ? 'Operación Exitosa!' : 'Error en la Operación'}
                            </h3>
                            <p style={{ marginBottom: '1.5rem', color: actionResult.success ? 'black' : 'red' }}>
                                {actionResult.message}
                            </p>
                            {actionResult.product && renderProductDetailsTable(actionResult.product)}
                            <Button type="primary" size="large" onClick={resetAllState}>
                                Nueva Acción
                            </Button>
                        </div>
                    )}
                </div> {/* End of steps-content */}
            </div>{/* End of bin-content-container */}
        </div>
        </ConfigProvider>
    );
}

export default ProductBin;